import { activityRecordTypeSingularLabels, TouchpointId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import {
    ActivityFeedRowBody,
    ActivityRowEditButton,
} from "components/details-page-v-next/activity-rows";
import { recentActivityItemDisplay } from "components/details-page-v-next/recent-activities-feed";
import { goalMetricItemHref } from "components/marketing-goals/goal-metric-item";
import { ActivityTypeOption } from "components/tasks/activity-type-options";
import { TouchpointTypeCell } from "components/tasks/touchpoint-type-cell";
import { defaultLinkStyle } from "components/ui/link";
import React from "react";
import { Link } from "react-router-dom";
import { MyActivityItem, myActivityRecordType } from "./my-activity";

type MyActivityQueryRow = inferProcedureOutput<
    AppRouter["touchpoint"]["myActivity"]
>[number];

export const toMyActivityItem = (row: MyActivityQueryRow): MyActivityItem => ({
    id: row.id,
    category: row.category,
    taskTypeId: row.taskTypeId ?? null,
    kind: row.kind,
    sourceId: row.sourceId ?? null,
    label: row.label,
    chipLabel: row.chipLabel ?? null,
    text: row.text ?? null,
    noteText: row.noteText ?? null,
    date: row.date,
    time: row.time,
    parentName: row.parentName ?? null,
    note: row.note ?? null,
    link: row.link ?? null,
});

interface MyActivityItemRowProps {
    item: MyActivityItem;
    activityTypes: ActivityTypeOption[];
    onSaved: () => Promise<void>;
    onEdit: (() => void) | null;
}

export const MyActivityItemRow: React.FC<MyActivityItemRowProps> = ({
    item,
    activityTypes,
    onSaved,
    onEdit,
}) => {
    const display = recentActivityItemDisplay({
        id: item.id,
        kind: item.kind,
        label: item.label,
        chipLabel: item.chipLabel,
        category: item.category,
        text: item.text,
        noteText: item.noteText,
        userName: null,
        occurredAt: `${item.date}T${item.time}`,
    });
    const recordType = myActivityRecordType(item);

    return (
        <ActivityFeedRowBody
            display={{
                ...display,
                link:
                    item.link && item.parentName ? (
                        <span className="inline-flex flex-wrap items-center gap-x-1.5">
                            <Link
                                to={goalMetricItemHref(item.link)}
                                className={defaultLinkStyle}>
                                {item.parentName}
                            </Link>
                            {recordType ? (
                                <span className="text-xs text-gray-500">
                                    · {activityRecordTypeSingularLabels[recordType]}
                                </span>
                            ) : null}
                        </span>
                    ) : null,
            }}
            actions={
                onEdit ? (
                    <ActivityRowEditButton onClick={onEdit} />
                ) : (
                    <TouchpointTypeCell
                        target={{
                            touchpointId: item.id as TouchpointId,
                            category: item.category,
                            taskTypeId: item.taskTypeId,
                            note: item.note,
                        }}
                        recordType={recordType}
                        activityTypes={activityTypes}
                        onSaved={onSaved}
                    />
                )
            }
        />
    );
};
